import Tooltip from "@mui/material/Tooltip"
import { LucideIcon, Pencil } from "lucide-react"
import { ForwardedRef, forwardRef } from "react"
import { selectedControlColor } from "./Toolbar"
import { styled } from "@mui/material"

type ToolbarButtonProps = {
  title: string
  onClick: React.MouseEventHandler<HTMLButtonElement> | undefined
  children: React.ReactNode
}

const baseButtonStyles = {
  borderRadius: 4,
  flexShrink: 0,
  background: "transparent",
  "&:hover": {
    background: "rgba(0, 0, 0, .05)",
  },
  "&:active": {
    background: "rgba(0, 0, 0, .1)",
  },
}

export function ToolbarButton({
  title,
  onClick,
  children,
}: ToolbarButtonProps) {
  return (
    <Tooltip title={title}>
      <ToolbarButtonEl onClick={onClick}>{children}</ToolbarButtonEl>
    </Tooltip>
  )
}

export const ToolbarButtonEl = styled("button")({
  ...baseButtonStyles,
  border: "none",
  padding: "0 8px",
  margin: 0,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
})

type ToolbarIconButtonProps = {
  selected?: boolean
  icon: LucideIcon
  onClick: React.MouseEventHandler<HTMLButtonElement> | undefined
  tooltip?: string
}

export const ToolbarIconButton = forwardRef(function ToolbarIconButton(
  { icon, selected, onClick, tooltip, ...other }: ToolbarIconButtonProps,
  forwardedRef: ForwardedRef<HTMLButtonElement>,
) {
  const Icon = icon
  return (
    <Tooltip title={tooltip}>
      <ToolbarIconButtonEl
        selected={selected}
        onClick={onClick}
        ref={forwardedRef}
        {...other}
      >
        <Icon size={18} color={selected ? "white" : "black"} />
      </ToolbarIconButtonEl>
    </Tooltip>
  )
})

type ToolbarIconButtonElProps = {
  selected?: boolean
}

const ToolbarIconButtonEl = styled("button")<ToolbarIconButtonElProps>({
  ...baseButtonStyles,
  width: 40,
  height: 40,
  flexGrow: 0,
  variants: [
    {
      props: { selected: true },
      style: {
        background: selectedControlColor,
        "&:hover": {
          background: selectedControlColor,
        },
      },
    },
  ],
})

const ToolbarSliverButtonEl = styled("button")<ToolbarIconButtonElProps>({
  ...baseButtonStyles,
  height: 24,
  border: "none",
  background: "transparent",
  "&:hover": {
    background: "rgba(0, 0, 0, .05)",
  },
  "&:active": {
    background: "rgba(0, 0, 0, .1)",
  },
  variants: [
    {
      props: { selected: true },
      style: {
        background: selectedControlColor,
        "&:hover": {
          background: selectedControlColor,
        },
      },
    },
  ],
})

export const ToolbarSliverButton = forwardRef(function ToolbarSliverButton(
  { icon, selected, onClick }: ToolbarIconButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const Icon = icon
  return (
    <ToolbarSliverButtonEl ref={ref} selected={selected} onClick={onClick}>
      <Icon
        size={18}
        color={selected ? "white" : "black"}
        style={{ pointerEvents: "none" }}
      />
    </ToolbarSliverButtonEl>
  )
})
